import { useEffect, useState } from "react";
import { useGame } from "../../net/gameClient";
import { hostTokenFor, rememberHostToken } from "../../api";
import type { PublicQuestion, Snapshot } from "../../../shared/types";

/** The host's phone — a compact control deck for a remote-control session. It
    authenticates with the session host token carried in the QR deep link
    (`/remote/<pin>#<token>`), then drives the whole game: start, reveal,
    leaderboard, next, mute and fullscreen (relayed to the big screen), and end.
    The big screen stays control-free; everything lives here. */
export default function RemoteControl({ pin }: { pin: string }) {
  // Capture the host token from the QR deep link once, persist it (so reconnects
  // and refreshes keep working), then strip it from the address bar.
  const [token] = useState<string | null>(() => {
    if (typeof location === "undefined") return null;
    const hash = location.hash.replace(/^#/, "").trim();
    if (hash) {
      rememberHostToken(pin, hash);
      return hash;
    }
    return hostTokenFor(pin);
  });
  useEffect(() => {
    if (location.hash) history.replaceState(null, "", location.pathname + location.search);
  }, []);

  // Keep the phone awake while it's the controller (best-effort).
  useEffect(() => {
    let lock: { release?: () => void } | null = null;
    const acquire = async () => {
      try {
        lock = await (navigator as unknown as { wakeLock?: { request: (t: string) => Promise<{ release?: () => void }> } })
          .wakeLock?.request("screen") ?? null;
      } catch {
        /* unsupported or denied */
      }
    };
    acquire();
    const onVis = () => {
      if (document.visibilityState === "visible") acquire();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      try {
        lock?.release?.();
      } catch {
        /* already gone */
      }
    };
  }, []);

  const game = useGame(pin, "host", token, { surface: "control" });
  const snap = game.snapshot;

  // Fullscreen is a guessed local intent (we auto-entered at launch, so start on).
  const [fsOn, setFsOn] = useState(true);
  const [confirmEnd, setConfirmEnd] = useState(false);

  const muted = !!snap?.musicMuted;
  const toggleMute = () => game.sendHost(muted ? "unmute" : "mute");
  const toggleFs = () => {
    game.sendHost(fsOn ? "exitFullscreen" : "fullscreen");
    setFsOn((v) => !v);
  };

  const wrap = (children: React.ReactNode) => (
    <div
      className="bk"
      style={{
        minHeight: "100dvh",
        height: "auto",
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        padding: "18px 16px calc(18px + env(safe-area-inset-bottom))",
      }}
    >
      {children}
    </div>
  );

  if (!snap) {
    return wrap(
      <div style={{ margin: "auto", textAlign: "center" }}>
        <div style={{ fontSize: 34 }}>🎛️</div>
        <p style={{ marginTop: 12, color: "var(--ink-soft)", fontWeight: 700 }}>
          {game.error ? game.error : game.connected ? "Picking up the controls…" : "Connecting…"}
        </p>
        {game.error && (
          <p style={{ marginTop: 8, color: "var(--ink-faint)", fontWeight: 600, fontSize: 13 }}>
            Re-scan the QR on the big screen to reconnect.
          </p>
        )}
      </div>,
    );
  }

  const paused = !!snap.paused;
  const phase = snap.phase;
  const last = snap.index + 1 >= snap.total;
  const q = snap.question;

  // The primary action for this phase.
  let primary: { label: string; action: () => void; disabled?: boolean; hint?: string } | null = null;
  if (phase === "lobby") {
    primary = {
      label: "Start quiz",
      action: () => game.sendHost("start"),
      disabled: snap.playerCount === 0,
      hint: snap.playerCount === 0 ? "Waiting for the first baker to join…" : undefined,
    };
  } else if (phase === "question") {
    primary = { label: "Reveal answer", action: () => game.sendHost("reveal") };
  } else if (phase === "reveal") {
    primary = { label: "Show leaderboard", action: () => game.sendHost("leaderboard") };
  } else if (phase === "leaderboard") {
    primary = {
      label: last ? "Final results" : "Next question",
      action: () => game.sendHost("next"),
    };
  }

  return wrap(
    <>
      {/* Header — what's running */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 26 }}>{snap.quizEmoji || "🎛️"}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="bk-eyebrow bk-eyebrow--red" style={{ fontSize: 11 }}>REMOTE CONTROL</div>
          <div style={{ fontWeight: 800, fontSize: 17, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {snap.quizTitle || game.quizTitle}
          </div>
        </div>
        <span
          className="bk-chip"
          style={{ fontSize: 12, padding: "6px 10px", flex: "0 0 auto", opacity: game.connected ? 1 : 0.5 }}
          title={game.connected ? "Connected" : "Reconnecting"}
        >
          <span className="bk-dot" style={{ background: game.connected ? "var(--good)" : "var(--ink-faint)" }} />
          {game.connected ? "Live" : "…"}
        </span>
      </div>

      {/* Status strip — PIN + players */}
      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
        <div className="bk-card" style={{ flex: 1, padding: "10px 14px" }}>
          <div className="bk-eyebrow" style={{ fontSize: 10 }}>GAME PIN</div>
          <div className="mono" style={{ fontWeight: 700, fontSize: 20, letterSpacing: ".06em", color: "var(--red)" }}>
            {formatPin(snap.pin)}
          </div>
        </div>
        <div className="bk-card" style={{ flex: 1, padding: "10px 14px" }}>
          <div className="bk-eyebrow" style={{ fontSize: 10 }}>{snap.mode === "teams" ? "BAKERS" : "PLAYERS"}</div>
          <div className="mono" style={{ fontWeight: 700, fontSize: 20 }}>{snap.playerCount}</div>
        </div>
      </div>

      {/* Phase context */}
      <div className="bk-card" style={{ marginTop: 14, padding: "16px 16px", flex: "0 0 auto" }}>
        <PhaseContext snap={snap} q={q} />
      </div>

      {paused && (
        <div
          className="bk-chip"
          style={{ marginTop: 12, width: "100%", justifyContent: "center", padding: "10px 12px", background: "var(--gold-soft, #fbf2dc)", border: "1px solid var(--gold, #d9a93a)", color: "#8a6a23", fontWeight: 800 }}
        >
          ⏸ Game paused — waiting for the big screen
        </div>
      )}

      {/* Spacer pushes the action deck to the bottom on tall screens */}
      <div style={{ flex: 1, minHeight: 14 }} />

      {/* Primary action */}
      {primary && (
        <>
          {primary.hint && (
            <p style={{ textAlign: "center", color: "var(--ink-faint)", fontWeight: 700, fontSize: 13, margin: "0 0 8px" }}>
              {primary.hint}
            </p>
          )}
          <button
            className="bk-btn bk-btn--primary"
            onClick={primary.action}
            disabled={primary.disabled || !game.connected}
            style={{ width: "100%", minHeight: 60, fontSize: 19, fontWeight: 800, opacity: primary.disabled ? 0.5 : 1 }}
          >
            {primary.label}
          </button>
        </>
      )}
      {phase === "podium" && (
        <div className="bk-card" style={{ padding: 18, textAlign: "center" }}>
          <div style={{ fontSize: 30 }}>🏆</div>
          <div style={{ fontWeight: 800, fontSize: 18, marginTop: 6 }}>Quiz complete!</div>
          {snap.players[0] && (
            <div style={{ color: "var(--ink-soft)", fontWeight: 700, marginTop: 4 }}>
              {snap.players[0].nickname} takes the crown
            </div>
          )}
          <div style={{ color: "var(--ink-faint)", fontWeight: 600, fontSize: 13, marginTop: 10 }}>
            You can close this tab.
          </div>
        </div>
      )}

      {/* Persistent control row */}
      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
        <CtrlButton onClick={toggleMute} active={!muted} label={muted ? "Music off" : "Music on"} icon={muted ? "🔇" : "🔊"} />
        <CtrlButton onClick={toggleFs} active={fsOn} label={fsOn ? "Full screen" : "Windowed"} icon="⛶" />
        {phase !== "podium" && (
          <CtrlButton onClick={() => setConfirmEnd(true)} label="End" icon="⏹" danger />
        )}
      </div>

      {confirmEnd && (
        <div
          onClick={() => setConfirmEnd(false)}
          style={{ position: "fixed", inset: 0, zIndex: 100, display: "grid", placeItems: "center", background: "rgba(40,25,15,.55)", padding: 22 }}
        >
          <div
            className="bk-card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 360, width: "100%", padding: 22, animation: "bk-pop-in .15s ease both" }}
          >
            <h2 style={{ fontSize: 20, fontWeight: 800 }}>End the quiz?</h2>
            <p style={{ color: "var(--ink-soft)", fontWeight: 600, marginTop: 8, lineHeight: 1.5 }}>
              This finishes the game for everyone and shows the final podium on the big screen.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 18 }}>
              <button
                className="bk-btn bk-btn--primary bk-btn--lg bk-btn--block"
                onClick={() => {
                  game.sendHost("end");
                  setConfirmEnd(false);
                }}
              >
                End for everyone
              </button>
              <button
                className="bk-btn bk-btn--block"
                onClick={() => setConfirmEnd(false)}
                style={{ background: "transparent", boxShadow: "none", color: "var(--ink-soft)" }}
              >
                Keep playing
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
  );
}

/** The middle card — phase-specific live context for the host's eyes. */
function PhaseContext({ snap, q }: { snap: Snapshot; q?: PublicQuestion }) {
  // Hook must run every render — compute the countdown unconditionally.
  const remaining = useCountdown(q?.endsAt);
  if (snap.phase === "lobby") {
    return (
      <div>
        <div style={{ fontWeight: 800, fontSize: 18 }}>Bakers are joining</div>
        <div style={{ color: "var(--ink-soft)", fontWeight: 600, marginTop: 4 }}>
          Players scan the big-screen QR or enter the PIN. Start when you're ready.
        </div>
      </div>
    );
  }
  if (snap.phase === "question" && q) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="bk-chip bk-chip--red" style={{ fontSize: 12, padding: "5px 10px" }}>
            <span className="mono" style={{ fontWeight: 700 }}>Q{snap.index + 1}</span>/{snap.total}
          </span>
          {remaining != null && (
            <span className="mono" style={{ marginLeft: "auto", fontWeight: 800, fontSize: 22, color: remaining <= 5 ? "var(--red)" : "var(--ink)" }}>
              {remaining}s
            </span>
          )}
        </div>
        <div style={{ fontWeight: 800, fontSize: 16, marginTop: 10, lineHeight: 1.3 }}>{q.prompt}</div>
        <div style={{ color: "var(--ink-soft)", fontWeight: 700, marginTop: 8 }}>
          <span className="mono" style={{ color: "var(--red)" }}>{q.answeredCount}</span> of {snap.playerCount} answered
        </div>
      </div>
    );
  }
  if (snap.phase === "reveal") {
    return (
      <div>
        <div className="bk-eyebrow bk-eyebrow--red">ANSWER REVEALED</div>
        <div style={{ fontWeight: 800, fontSize: 18, marginTop: 6 }}>
          {snap.reveal?.correctPct ?? 0}% got it right
        </div>
        <div style={{ color: "var(--ink-soft)", fontWeight: 600, marginTop: 4 }}>
          Question {snap.index + 1} of {snap.total}
        </div>
      </div>
    );
  }
  if (snap.phase === "leaderboard") {
    const top = snap.players.slice(0, 3);
    return (
      <div>
        <div className="bk-eyebrow bk-eyebrow--red">AFTER QUESTION {snap.index + 1} OF {snap.total}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
          {top.map((p, i) => (
            <div key={p.nickname} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className={`bk-rank bk-rank--${i + 1}`} style={{ width: 26, height: 26, fontSize: 13 }}>{i + 1}</span>
              <span style={{ fontWeight: 700, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {p.emoji ? `${p.emoji} ` : ""}{p.nickname}
              </span>
              <span className="mono" style={{ fontWeight: 700 }}>{p.score.toLocaleString()}</span>
            </div>
          ))}
          {top.length === 0 && <span style={{ color: "var(--ink-faint)", fontWeight: 700 }}>No scores yet.</span>}
        </div>
      </div>
    );
  }
  if (snap.phase === "podium") {
    return (
      <div>
        <div className="bk-eyebrow bk-eyebrow--red">FINAL</div>
        <div style={{ fontWeight: 800, fontSize: 18, marginTop: 6 }}>The bake is done</div>
      </div>
    );
  }
  return null;
}

/** Live countdown in whole seconds from an epoch-ms deadline. */
function useCountdown(endsAt?: number): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!endsAt) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [endsAt]);
  if (!endsAt) return null;
  return Math.max(0, Math.ceil((endsAt - now) / 1000));
}

function CtrlButton({
  onClick,
  label,
  icon,
  active,
  danger,
}: {
  onClick: () => void;
  label: string;
  icon: string;
  active?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="bk-card"
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        padding: "12px 6px",
        cursor: "pointer",
        minHeight: 64,
        border: danger ? "1px solid var(--red-soft)" : active ? "1px solid var(--good)" : "1px solid var(--line)",
        background: danger ? "var(--red-tint)" : "var(--surface)",
        color: danger ? "var(--red)" : "var(--ink)",
      }}
    >
      <span style={{ fontSize: 20, lineHeight: 1 }}>{icon}</span>
      <span style={{ fontSize: 12, fontWeight: 800 }}>{label}</span>
    </button>
  );
}

function formatPin(pin: string): string {
  return pin.length === 6 ? `${pin.slice(0, 3)} ${pin.slice(3)}` : pin;
}
